import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationRaw,
} from "vue-router";
import routes from "@/router/routes";
import store from "@/store";
import { UserRoles } from "@/enums/UserRoles";
import LocalStorageService from "@/services/localStorage.service";
import { trackRouter } from "vue-gtag-next";
import { ROUTER_SCROLL_BEHAVIORS } from "@/constants/scrollBehaviors";
import handleCustomSubdomainRedirect from "@/helpers/router/handleCustomSubdomainRedirect";
import { hasRequiredTier } from "@/helpers/getUserTier";
import { UserTier } from "@/enums/UserTier";
const customerRoutes = [
  "EstateMap",
  "EOIRequest",
  "EOIRequestPayment",
  "EOIWaitlist",
  "Builder",
  "RequestQuote",
  "PublicProjectPage",
  "PublicProjectPageCanonical",
  "PublicProjectPageShort",
];
let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.meta?.customScroll) return;

    if (to.meta.scrollBehavior === ROUTER_SCROLL_BEHAVIORS.INSTANT) {
      return { top: 0 };
    }

    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, behavior: "smooth" };
    }
  },
});

router.beforeEach(async (to, from, next) => {
  const redirectTo = handleCustomSubdomainRedirect(to);

  if (redirectTo) {
    next(redirectTo);
    return;
  }

  if (customerRoutes.includes(to.name as string)) {
    changeFavIconToDark();
    changeTabTitle(to);
  } else {
    changeIconAndTitleToDefault();
  }

  if (
    process.env.VUE_APP_IS_IN_MAINTENANCE === "true" &&
    to.name !== "Maintenance"
  ) {
    next({ name: "Maintenance" });
    return;
  }

  const isAuthRoute = to?.meta.access;
  const isLoginRoute = to?.meta.isLogin;
  const tierRequired = to?.meta.tierRequired;

  if (store.state.user.accessToken) {
    if (store.state.user.redirectLink) {
      const link = store.state.user.redirectLink;

      store.commit("user/clearRedirectLink");
      next(link);
      return;
    }

    if (isLoginRoute) {
      next(toDefaultPage());
      return;
    }

    const isV2User = await hasRequiredTier(
      store.state.user.data,
      UserTier.TIER_1
    );

    const excludedPaths = ["/auth", "/residential-communities", "/p/", "/s/"];
    const shouldExclude = excludedPaths.some((path) =>
      to.path.startsWith(path)
    );

    if (isV2User && !to.path.startsWith("/v2") && !shouldExclude) {
      next("/v2");
      return;
    }

    const hasRoleAccess =
      !to.meta.access ||
      (to.meta.access as string[]).includes(store.state.user.data.role);
    const hasTierAccess =
      !tierRequired ||
      (await hasRequiredTier(store.state.user.data, tierRequired as UserTier));

    if (hasRoleAccess && hasTierAccess) {
      next();
      return;
    } else {
      next(toDefaultPage());
      return;
    }
  }

  if (isAuthRoute) {
    return setRedirectLinkAndMoveOn(from, to, next);
  }

  if (
    !to.params.unauthorized &&
    to.name !== "SignIn" &&
    to.name !== "RegistrationVerifyView"
  ) {
    store.commit("user/clearRedirectLink");
  }

  cleanEoiUserData(from, to);
  next();
});

trackRouter(router);

function cleanEoiUserData(
  from: RouteLocationNormalized,
  to: RouteLocationNormalized
) {
  if (from.name === "EOIRequest" && to.name !== "EOIRequestPayment") {
    LocalStorageService.remove("eoiUserData");
  }
}

function toDefaultPage(): RouteLocationRaw {
  if (
    store.state.user.data.role === UserRoles.LANDOWNER ||
    store.state.user.data.role === UserRoles.SALES_PERSON
  ) {
    return { name: "Dashboard" };
  }

  if (store.state.user.data.role === UserRoles.SUPER_ADMIN) {
    return { name: "AdminUsers" };
  }

  if (store.state.user.data.role === UserRoles.CUSTOMER) {
    return { name: "CustomerLots" };
  }

  store.dispatch("user/logout");
  return { path: "/" };
}

function changeFavIconToDark() {
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.getElementsByTagName("head")[0].appendChild(link);
  }

  link.href = "/favicon-dark.ico";
}

function changeTabTitle(to: RouteLocationNormalized) {
  if (to.params.estateId) {
    store.dispatch("estate/getEstate", to.params.estateId).then((res) => {
      document.title = res.data.title;
    });
  }
}

function changeIconAndTitleToDefault() {
  link.href = "/favicon.ico";
  document.title = "LotTrackr";
}

function setRedirectLinkAndMoveOn(
  from: RouteLocationNormalized,
  to: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  store.commit("user/setRedirectLink", to);

  if (from.name === "EstateMap") {
    return next({
      name: "EstateMap",
      params: { estateId: from.params.estateId, unauthorized: "true" },
      query: from.query,
    });
  }

  if (to.name === "EOIWaitlist") {
    return next({
      name: "EstateMap",
      params: { estateId: to.params.estateId, unauthorized: "true" },
    });
  }

  return next({ name: "SignIn" });
}

export default router;
